<template>
  <div
    class="StepsNavSimple"
    :class="{ 'StepsNav--narrow': depth === 2 }"
  >
    <ul class="StepsNavSimple__list">
      <li
        v-for="stepItem in steps"
        :key="stepItem.uuid"
        class="StepsNavSimple__item"
      >
        <div
          class="SNOrb"
          :class="{
            'SNOrb--active': stepItem.short === active,
            jsHover: stepItem.short === hover,
            'SNOrb--available': isAvailable(stepItem.uuid),
            'SNOrb--finished': isCompleted(stepItem.uuid),
            'SNOrb--skipped': isSkipped(stepItem.uuid)
          }"
        >
          <span class="SNOrb__label">
            <b-badge variant="dark">
              {{ `${depth === 2 ? stepItem.short.split('-')[1]: stepItem.short }. ${$t(stepItem.label[$i18n.locale] || stepItem.label)}` }}
            </b-badge>
          </span>
          <span
            class="SNOrb__step"
            @mouseover="setHover(stepItem.short)"
            @mouseleave="setHover"
            @click="setActive(stepItem.uuid, stepItem.short)"
          >{{ depth === 2 ? stepItem.short.split('-')[1]: stepItem.short }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>

/**
 * TODO: This component needs a refactor in variable naming & structure
 * TODO: Currently it has been patched up to do a job, but it's not sustainable code
 */

import { mapGetters } from 'vuex'

export default {
  name: 'StepsNavSimple',
  props: {
    step: {
      type: [String, Number],
      default: 1,
    },
    steps: {
      type: Array,
      default: () => []
    },
    record: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 1,
      // TODO: Only supports 1 or 2
      validator(value) {
        return [1, 2].includes(value)
      },
    },
  },
  data() {
    return {
      hover: null,
      activeStep: 0
    }
  },
  computed: {
    ...mapGetters('processes', ['getSkippedStepUuids']),
    stepsByUuid() {
      return this.steps.reduce((result, step) => {
        result[step.uuid] = step
        return result
      }, {})
    },
    active() {
      const stepValue = this.activeStep || this.step?.toString()
      // UUID-based navigation: resolve to short number for comparison
      const byUuid = this.steps?.find(s => s.uuid === stepValue)
      if (byUuid) return String(byUuid.short).split('-')[0]
      if (this.depth === 1 && !this.activeStep) {
        return (this.step+'').split('-')[0]
      }
      return this.activeStep || this.step

      // Used previously to enforce min / max based on available number of steps
      // return Math.max(1, Math.min(this.items.length, parseInt(this.step, 10))) - 1
    },
    parent() {
      if (this.depth === 1) {
        return null
      }
      return (this.step+'').split('-')[0]
    },
    // TODO: Only supports 1 or 2
    items() {
      let childrenByParent = {}
      let children = []
      let root = []

      this.steps.forEach(step => {
        if (step.children) {
          children = children.concat(step.children)
          let parent = ((step.short || step.step) + '').split('-')[0] || 'unknown'
          childrenByParent[parent] = step.children
        }
      })
      this.steps.forEach(step => {
        if (! children.includes(step.uuid)) {
          root.push(step)
        }
      })

      if (this.depth === 1) {
        // console.log("ROOT ITEMS", root)
        return root
      }

      // console.log(this.depth, childrenByParent, this.parent, childrenByParent[this.parent])

      let items = (childrenByParent[this.parent] || []).map(uuid => this.stepsByUuid[uuid])
      // console.log("CHILD ITEMS", items)
      return items

      // return steps.map(step => step.label)
    },
    recordState() {
      try {
        return this.record.flow.state
      } catch (e) {
        return []
      }
    },
    completedUuids() {
      return this.recordState
        .filter(state => state.completed_at)
        .map(state => state.uuid)
    },
    availableUuids() {
      return this.recordState
        .filter(state => state.started_at)
        .map(state => state.uuid)
    },
    routeType () {
      return this.record.processType.value
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler (route) {
        this.activeStep = route.query?.step?.toString()
      }
    }
  },
  methods: {
    isCompleted(uuid) {
      if (this.getSkippedStepUuids.includes(uuid)) return false
      return this.completedUuids.includes(uuid)
    },
    isAvailable(uuid) {
      if (this.getSkippedStepUuids.includes(uuid)) return false
      return this.availableUuids.includes(uuid)
    },
    isSkipped(uuid) {
      return this.getSkippedStepUuids.includes(uuid)
    },
    setHover(item) {
      if (item >= 0) {
        this.hover = item
      } else {
        this.hover = null
      }
    },
    setActive(uuid, short) {
      if (!this.isAvailable(uuid) && !this.isSkipped(uuid)) return null;

      if (['private', 'brussels', 'wallonia', 'takeover'].includes(this.routeType)) {
        this.$router.push({
          name: `${this.routeType}Steps`,
          query: {
            step: short,
          },
        })
      } else {
        this.$router.push({
          name: `${this.routeType}Step${short}`,
          params: {
            step: short,
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
  $orb-size: 30px;
  $orb-big-scale: 1.75;
  $orb-color: $gray-400;
  $timing: 0.1s;

.StepsNavSimple {
  width: 100%;
  text-align: left;
  user-select: none;

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
  }
  &__item {
    display: inline-block;
    flex-basis: 100%;
    flex-grow: 1;
    flex-shrink: 1;

    &:first-child,
    &:last-child {
      .SNOrb__label {
        margin: 0;
        transform: none;
      }
      .SNOrb--active {
        .SNOrb__step {
          transform: none;
        }
      }
    }

    &:last-child {
      flex-basis: auto;

      .SNOrb__label {
        right: 0;
      }
      .SNOrb--active {
        .SNOrb__step {
          margin-left: -(($orb-size * $orb-big-scale)-$orb-size)
        }
      }
    }
  }


}

// SNOrb — shared between StepsNavSimple and StepsNavFork
.SNOrb {
  display: block;
  width: 100%;
  position: relative;
  padding-top: ($orb-size * 0.5);
  padding-bottom: ($orb-size * 0.5);
  transition-property: padding-top, padding-bottom;
  transition-timing-function: ease-in-out;
  transition-duration: $timing;

  &:before {
    content: "";
    display: block;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 2px;
    width: 100%;
    background-color: $orb-color;
    z-index: -1;
    transition: background-color, $timing;
  }

  &__label {
    position: absolute;
    bottom: 100%;
    color: rgba(0, 0, 0, 0.6);
    display: none;
    margin-left: 0.5 * $orb-size;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  &__step {
    cursor: not-allowed;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    width: $orb-size;
    height: $orb-size;
    border-radius: $orb-size;
    background-color: $orb-color;
    color: $gray-800;
    font-size: 0.85em;
    font-weight: bold;
    transition-property: width, height, background-color, color, transform, margin-left;
    transition-timing-function: ease-in-out;
    transition-duration: $timing;
  }

  &--available {
    .SNOrb__step {
      cursor: pointer;
      background-color: $gray-800;
      color: white;
    }
    &:before {
      background-color: $gray-800;
    }
  }

  &--skipped {
    .SNOrb__step {
      cursor: pointer;
      background-color: $gray-300;
      color: $gray-600;
      border: 2px dashed $gray-500;
    }
    &:before {
      background-color: $gray-400;
    }
  }


  &--finished {
    .SNOrb__step {
      cursor: pointer;
      background-color: $gray-800;
      color: white;
    }
    &:before {
      background-color: $gray-800;
    }
  }

  &--active {
    padding: 0;
    pointer-events: none;
    .SNOrb__step {
      width: $orb-size * $orb-big-scale;
      height: $orb-size * $orb-big-scale;
      background-color: $gray-800;
      color: white;
      transform: translateX(-0.5 * (($orb-size * $orb-big-scale)-$orb-size));
    }
    .SNOrb__label {
      margin-left: 0.5 * ($orb-size * $orb-big-scale);
    }
  }

  &.jsHover {
    .SNOrb__label {
      display: inline-block;
    }
  }
}


</style>
